import {
  channelLabel,
  channelsToKeyArray,
  fetchAllChannels,
  sortChannels,
  type ChannelRow,
} from './channels';
import { isCreatedOnOrAfterDay, startOfTrendWindow } from './connectionDashboard';
import { subscriberEmailStatus } from './subscriberEmailStatus';
import { supabase } from './supabase';

export type ChannelMetricCount = {
  total: number;
  delta14Days: number;
};

export type ChannelDistributionRow = {
  slug: string;
  label: string;
  total: ChannelMetricCount;
  confirmed: ChannelMetricCount;
  unsubscribed: ChannelMetricCount;
  pending: ChannelMetricCount;
};

export type ChannelDistributionDashboardData = {
  rows: ChannelDistributionRow[];
};

type SubscriberChannelRow = {
  channels?: unknown;
  email_status?: string | null;
  status?: string | null;
  created_at?: string | null;
  unsubscribed_at?: string | null;
};

type MutableChannelStats = {
  total: number;
  confirmed: number;
  unsubscribed: number;
  pending: number;
  added14Days: number;
  lost14Days: number;
  confirmedAdded14Days: number;
  unsubscribedAdded14Days: number;
  pendingAdded14Days: number;
};

function emptyStats(): MutableChannelStats {
  return {
    total: 0,
    confirmed: 0,
    unsubscribed: 0,
    pending: 0,
    added14Days: 0,
    lost14Days: 0,
    confirmedAdded14Days: 0,
    unsubscribedAdded14Days: 0,
    pendingAdded14Days: 0,
  };
}

function metricCount(total: number, delta14Days: number): ChannelMetricCount {
  return { total, delta14Days };
}

/** Aggregate per-channel subscriber counts from loaded subscriber rows. */
export function aggregateChannelDistribution(
  subscriberRows: SubscriberChannelRow[],
  catalog: ChannelRow[],
  windowStart: Date,
): ChannelDistributionRow[] {
  const statsBySlug = new Map<string, MutableChannelStats>();

  for (const row of subscriberRows) {
    const slugs = channelsToKeyArray(row.channels);
    if (slugs.length === 0) continue;

    const status = subscriberEmailStatus(row).toLowerCase();
    const isConfirmed = status === 'confirmed';
    const isUnsubscribed = status === 'unsubscribed';
    const isPending = status === 'pending';
    const addedRecently =
      typeof row.created_at === 'string' && isCreatedOnOrAfterDay(row.created_at, windowStart);
    const unsubscribedRecently =
      isUnsubscribed &&
      typeof row.unsubscribed_at === 'string' &&
      isCreatedOnOrAfterDay(row.unsubscribed_at, windowStart);

    for (const slug of slugs) {
      const stats = statsBySlug.get(slug) ?? emptyStats();
      stats.total += 1;
      if (isConfirmed) stats.confirmed += 1;
      if (isUnsubscribed) stats.unsubscribed += 1;
      if (isPending) stats.pending += 1;
      if (addedRecently) {
        stats.added14Days += 1;
        if (isConfirmed) stats.confirmedAdded14Days += 1;
        if (isPending) stats.pendingAdded14Days += 1;
      }
      if (unsubscribedRecently) stats.unsubscribedAdded14Days += 1;
      if (unsubscribedRecently) stats.lost14Days += 1;
      statsBySlug.set(slug, stats);
    }
  }

  const catalogSlugs = new Set(catalog.map((channel) => channel.slug));
  const orderedSlugs = [
    ...sortChannels(catalog).map((channel) => channel.slug),
    ...[...statsBySlug.keys()]
      .filter((slug) => !catalogSlugs.has(slug))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })),
  ];

  return orderedSlugs.map((slug) => {
    const stats = statsBySlug.get(slug) ?? emptyStats();
    return {
      slug,
      label: channelLabel(slug, catalog),
      total: metricCount(stats.total, stats.added14Days - stats.lost14Days),
      confirmed: metricCount(stats.confirmed, stats.confirmedAdded14Days),
      unsubscribed: metricCount(stats.unsubscribed, stats.unsubscribedAdded14Days),
      pending: metricCount(stats.pending, stats.pendingAdded14Days),
    };
  });
}

export async function fetchChannelDistributionDashboardData(): Promise<ChannelDistributionDashboardData> {
  const windowStart = startOfTrendWindow(14);

  const [channelsResult, subscribersResult] = await Promise.all([
    fetchAllChannels(),
    supabase
      .from('subscribers')
      .select('channels, email_status, created_at, unsubscribed_at')
      .limit(20000),
  ]);

  if (subscribersResult.error) {
    throw new Error(subscribersResult.error.message || 'Failed to load subscribers for channel distribution');
  }

  const rows = aggregateChannelDistribution(
    (subscribersResult.data ?? []) as SubscriberChannelRow[],
    channelsResult,
    windowStart,
  );

  return { rows };
}
